import * as $ from 'jquery';
import { disposableSubscription, Lazy } from 'common/barrels/core';
import { Observable, Subject, Subscription } from 'common/barrels/rx';
import { Map } from 'immutable';

import { ContainerControl } from 'runtime/controls/containercontrol';
import { ControlArgs } from 'runtime/controls/controlargs';
import { RootContainer } from 'runtime/controls/rootcontainer';
import { registerBinding } from 'runtime/bindingengine';
import { applyBindings } from 'runtime/bindingengine';
import { ViewInfoRequested } from 'runtime/events/viewinforequested';
import { ViewRequested } from 'runtime/events/viewrequested';
import { MethodRouter } from 'runtime/methodrouter';

import 'runtime/styles/controls/viewhost.less';

export class ViewHost extends ContainerControl {
    private $container: JQuery;
    private $view: JQuery;
    private currentViewName: string;

    private viewInfo: Lazy<ViewInfoRequested> = new Lazy(() => this.eventAggregator.publish(new ViewInfoRequested()));

    private viewMap = Map<string, JQuery>();
    private $viewHoldingArea: JQuery;
    
    private viewNameValue = new Subject<string>();
    private contextValue = new Subject<string>();
    private viewContextSubscription: Subscription

    constructor(args: ControlArgs) {
        super(args);

        this.methods.register(this.config.elementName, this);

        this.$element.addClass('control-container viewhost');

        this.$container = this.prepareContainer();

        this.$viewHoldingArea = $('<div />').addClass('viewhost-holdingarea').appendTo(this.$element);

        this.applyTooltip(this.$element);

        //calling subsribe outside of disposableSubription allows displayView to run 
        //at least once on every popup re-open
        this.viewContextSubscription = this.viewNameValue
                .distinctUntilChanged()
                .combineLatest(this.contextValue.distinctUntilChanged(), (n, c) => ({ name: n, context: c }))
                .subscribe(x => this.displayView(x.name, x.context));

        this.subscriptions = disposableSubscription(
            this.processBindings(this.config),
            this.viewContextSubscription
        );

        if (!!this.config['viewName'])
            this.viewName(this.config['viewName']);

        if (this.config['alwaysuserootcontext']) {
            this.context('00000000-0000-0000-0000-000000000000');
        } else if (!!this.config['context']) {
            this.context(this.config['context']);
        } else if (!this.hasBinding('context')) {
            this.context(this.fieldContext);
        }

        this.initialize();
    }

    private prepareContainer() {
        var $container = $('<div />')
            .addClass('viewhost-container')
            .addClass(this.config.isScrollable ? 'viewhost-container-scrollable' : 'viewhost-container-notscrollable')
            .appendTo(this.$element);

        var $sizing = $('<div />')
            .appendTo($container)
            .addClass('viewhost-sizing');

        return $sizing;
    }

    viewName(value) {
        this.viewNameValue.next(value);
    }

    context(value) {
        this.contextValue.next(value);
    }

    displayView(value: string, context: string) {
        if (value === null || value === undefined || value === '' || value == this.currentViewName)
            return;

        if (this.viewMap.has(value)) {
            this.replaceView(this.viewMap.get(value), value);
            if (this.$view)
                this.applyContainerSizeAndActivate();

            this.reapplyEnabledState();
        }
        else {
            this.eventAggregator.publish(new ViewRequested(value, view => {
                const $view = $(view);
                this.replaceView($view, value);
                const methods = new MethodRouter(this.methods, value);
                $view.owns(methods);
                applyBindings(this.$view, context, this.eventAggregator, methods, this.values, this.resources, this, this.serializer);
                if (this.$view)
                    this.applyContainerSizeAndActivate();

                this.reapplyEnabledState();

                this.viewMap = this.viewMap.set(value, $view);
            }));
        }
    }

    private applyContainerSizeAndActivate() {
        var root = <RootContainer>this.$view.data('control');

        const children = this.$view.children().toArray().map(x => $(x).data('control'));

        const maxOrZero = (items: number[]) => items.length > 0 ? Math.max.apply(Math, items) : 0;
        const maxRight = maxOrZero(children.map(x => x.rightEdge()));
        const maxBottom = maxOrZero(children.map(x => x.bottomEdge()));

        this.$container
            .css('min-width', Math.max(this.config.minimumInteriorWidth, maxRight))
            .css('min-height', Math.max(this.config.minimumInteriorHeight, maxBottom));

        root.activate();
    }

    private replaceView(newView: JQuery, newViewName: string) {
        if (this.$view) {
            <RootContainer>this.$view.data('control').deactivate();
            this.$viewHoldingArea.append(this.$view);
        }

        this.$view = newView;

        if (this.$view) {
            this.$view.appendTo(this.$container);
        }

        this.currentViewName = newViewName;
    }

    // not the same as legacy, but not sure that behaviour can be reproduced without more extensive focus work
    focus() { }

    protected applyScrollVetoBehavior() {
        if (!this.config.isScrollable)
            this.installScrollVeto(this.$element);
    }

    applyEnabledState(value: boolean) {
        this.propagateEnabledStateToChildElements(this.$container, this.writableAgnosticDerivedEnabledState());
    }

    resizeStream(): Observable<{}> {
        return this.stretches() ? this.container.resizeStream() : Observable.never();
    }
}

registerBinding('viewhost', ViewHost);